import { getValueNodeOutputType, type PortDef, type UIRow } from './nodeDefs.js';
import { defineRenderNode, type RenderNodeDefsSubset } from './renderNodeDefs.js';
import { DEG2RAD, TWO_PI } from '../math/angle.js';

/** The most bands one `Bands` node chains (nodes.rs MOST_BANDS). */
const MOST_BANDS = 8;
const BAND_STOP_RANGE = { min: 0, max: 512, step: 0.5, subtype: 'distance' } as const;

/** How many bands a `Bands` node chains: its `count`, one to eight, two when unset (nodes.rs band_count). */
export function bandCount(value: unknown): number {
  const n = Math.round(Number(value));
  return Number.isFinite(n) ? Math.max(1, Math.min(MOST_BANDS, n)) : 2;
}

/** `distance`, then each stop followed by the colour of the band it opens, then the last stop (nodes.rs band_input_ports). */
function bandInputPorts(count: number): PortDef[] {
  const ports: PortDef[] = [{ name: 'distance', label: 'Distance', type: 'float' }];
  for (let index = 0; index < count; index++) {
    ports.push({ name: `stop${index}`, label: `Stop ${index}`, type: 'float', range: BAND_STOP_RANGE });
    ports.push({ name: `color${index}`, label: `Color ${index}`, type: 'vec4' });
  }
  ports.push({ name: `stop${count}`, label: `Stop ${count}`, type: 'float', range: BAND_STOP_RANGE });
  return ports;
}

const BAND_PORT_ROWS: UIRow[] = bandInputPorts(MOST_BANDS).map(port => ({ kind: 'port', ref: port.name }));

export const SOURCE_NODE_DEFS: RenderNodeDefsSubset<'Value' | 'Float' | 'Vector2' | 'Vector3' | 'Vector4' | 'Color' | 'SamplePoint' | 'Viewport' | 'LayerSize' | 'Distance' | 'MiterDistance' | 'SmoothDistance' | 'ArcLength' | 'ShapeNormal' | 'ShapeCoverage' | 'Theme' | 'Mask' | 'Blur' | 'Bands' | 'LayoutBounds' | 'ContourBounds' | 'ShapeTransform'> = {
  Value: defineRenderNode({
    label: 'Value', cat: 'source',
    inputs: [],
    outputs: node => [{ name: 'value', label: 'Value', type: getValueNodeOutputType(node) }],
  }),
  Float: defineRenderNode({
    label: 'Float', cat: 'source', subcat: 'value',
    inputs: [],
    outputs: [{ name: 'value', label: 'Value', type: 'float' }],
  }),
  Vector2: defineRenderNode({
    label: 'Vector2', cat: 'source', subcat: 'value',
    inputs: [],
    outputs: [{ name: 'value', label: 'Value', type: 'vec2' }],
  }),
  Vector3: defineRenderNode({
    label: 'Vector3', cat: 'source', subcat: 'value',
    inputs: [],
    outputs: [{ name: 'value', label: 'Value', type: 'vec3' }],
  }),
  Vector4: defineRenderNode({
    label: 'Vector4', cat: 'source', subcat: 'value',
    inputs: [],
    outputs: [{ name: 'value', label: 'Value', type: 'vec4' }],
  }),
  Color: defineRenderNode({
    label: 'Color', cat: 'source', subcat: 'value',
    inputs: [],
    outputs: [{ name: 'value', label: 'Value', type: 'vec4' }],
  }),
  SamplePoint: defineRenderNode({
    label: 'Sample Point', cat: 'source',
    inputs: [],
    outputs: [
      { name: 'point', label: 'Point', type: 'vec2' },
      { name: 'x', type: 'float' },
      { name: 'y', type: 'float' }
    ],
  }),
  Viewport: defineRenderNode({
    label: 'Viewport', cat: 'source',
    inputs: [],
    outputs: [
      { name: 'pixels per sample unit', label: 'Pixels per sample unit', type: 'float' },
      { name: 'pixels per sample unit x', label: 'Pixels per sample unit X', type: 'float' },
      { name: 'pixels per sample unit y', label: 'Pixels per sample unit Y', type: 'float' },
      { name: 'pixels per shape unit', label: 'Pixels per shape unit', type: 'float' },
      { name: 'pixels per shape unit x', label: 'Pixels per shape unit X', type: 'float' },
      { name: 'pixels per shape unit y', label: 'Pixels per shape unit Y', type: 'float' },
    ],
    graphs: ['material']
  }),
  LayerSize: defineRenderNode({
    label: 'Layer Size', cat: 'source',
    inputs: [],
    outputs: [
      { name: 'size', label: 'Size', type: 'vec2' },
      { name: 'width', label: 'Width', type: 'float' },
      { name: 'height', label: 'Height', type: 'float' },
    ],
    graphs: ['geometry']
  }),
  Distance: defineRenderNode({
    label: 'Distance', cat: 'source',
    inputs: [],
    outputs: [{ name: 'distance', label: 'Distance', type: 'float' }],
    graphs: ['material']
  }),
  // The distance with mitred corners: past a convex corner it grows along
  // the corner's bisector instead of round it (crates/graph/src/runtime.rs).
  MiterDistance: defineRenderNode({
    label: 'Miter Distance', cat: 'source',
    inputs: [],
    outputs: [{ name: 'distance', label: 'Distance', type: 'float' }],
    graphs: ['material']
  }),
  // The distance through a smooth minimum of radius `k` (data, not a port:
  // the field is computed before any material node runs).
  SmoothDistance: defineRenderNode({
    label: 'Smooth Distance', cat: 'source',
    inputs: [],
    outputs: [{ name: 'distance', label: 'Distance', type: 'float' }],
    graphs: ['material'],
    ui: [
      { kind: 'dragger', field: 'k', label: 'Radius', min: 0, max: 100, step: 0.1, subtype: 'distance' },
      { kind: 'port', ref: 'distance' },
    ],
  }),
  // How far along its contour a sample sits, and the contour's length, in shape units.
  ArcLength: defineRenderNode({
    label: 'Arc Length', cat: 'source',
    inputs: [],
    outputs: [
      { name: 'length', label: 'Length', type: 'float' },
      { name: 'contourLength', label: 'Contour length', type: 'float' },
    ],
    graphs: ['material']
  }),
  ShapeNormal: defineRenderNode({
    label: 'Shape Normal', cat: 'source',
    inputs: [],
    outputs: [
      { name: 'normal', label: 'Normal', type: 'vec2' },
      { name: 'x', label: 'X', type: 'float' },
      { name: 'y', label: 'Y', type: 'float' },
    ],
    graphs: ['material']
  }),
  ShapeCoverage: defineRenderNode({
    label: 'Shape Coverage', cat: 'source',
    inputs: [],
    outputs: [{ name: 'coverage', label: 'Coverage', type: 'float' }],
    graphs: ['material']
  }),
  Theme: defineRenderNode({
    label: 'Theme', cat: 'source',
    inputs: [],
    outputs: [
      { name: 'mode', label: 'Mode', type: 'float' },
      { name: 'resolvedTheme', label: 'Resolved theme', type: 'float' },
    ],
    graphs: ['material']
  }),
  Mask: defineRenderNode({
    label: 'Mask', cat: ['source', 'sdf'],
    inputs: [
      { name: 'distance', label: 'Distance', type: 'float' },
      { name: 'width', label: 'Width', type: 'float' },
      { name: 'coverage', label: 'Coverage', type: 'float', optional: true },
    ],
    outputs: [
      { name: 'fill', label: 'Fill', type: 'float' },
      { name: 'stroke', label: 'Stroke', type: 'float' },
      { name: 'outer', label: 'Outer', type: 'float' },
      { name: 'inner', label: 'Inner', type: 'float' },
    ],
    graphs: ['material'],
    ui: [
      { kind: 'port', ref: 'distance' },
      { kind: 'port', ref: 'width' },
      { kind: 'port', ref: 'coverage' },
      { kind: 'port', ref: 'fill' },
      { kind: 'port', ref: 'stroke' },
      { kind: 'port', ref: 'outer' },
      { kind: 'port', ref: 'inner' },
      { kind: 'dragger', field: 'width', label: 'Width', min: 0.5, max: 50, step: 0.1, subtype: 'distance' },
    ]
  }),
  // The shape's coverage blurred by a Gaussian, as a browser blurs a shadow
  // (fluveria-engine kb:decision/124): a glow, a shadow or a soft fill.
  Blur: defineRenderNode({
    label: 'Blur', cat: ['source', 'sdf'],
    inputs: [
      { name: 'radius', label: 'Radius (px)', type: 'float' },
      { name: 'intensity', label: 'Intensity', type: 'float' },
    ],
    outputs: [{ name: 'blur', label: 'Blur', type: 'float' }],
    graphs: ['material']
  }),
  // A chain of `count` coloured bands about the edge, each between two
  // stops, sharing their feathers (nodes.rs band_edges, band_chain).
  Bands: {
    label: 'Bands', cat: ['source', 'sdf'],
    inputs: node => bandInputPorts(bandCount(node.data.count)),
    outputs: [{ name: 'color', label: 'Color', type: 'vec4' }],
    graphs: ['material'],
    minWidth: 170,
    ui: [
      { kind: 'select', field: 'count', label: 'Bands', default: '2', options: Array.from({ length: MOST_BANDS }, (_, index) => ({ label: String(index + 1), value: String(index + 1) })) },
      ...BAND_PORT_ROWS,
      { kind: 'port', ref: 'color' },
    ],
  },
  LayoutBounds: defineRenderNode({
    label: 'Layout Bounds', cat: 'source',
    inputs: [],
    outputs: [{ name: 'layoutBounds', label: 'Layout bounds', type: 'bounds' }],
    graphs: ['material']
  }),
  ContourBounds: defineRenderNode({
    label: 'Contour Bounds', cat: 'source',
    inputs: [],
    outputs: [{ name: 'contourBounds', label: 'Contour bounds', type: 'bounds' }],
    graphs: ['material']
  }),
  ShapeTransform: defineRenderNode({
    label: 'Shape Transform', cat: 'source',
    inputs: [],
    outputs: [
      { name: 'transform', label: 'Transform', type: 'transform' },
      { name: 'center', label: 'Center', type: 'vec2' },
      { name: 'angle', label: 'Angle', type: 'float', range: { min: 0, max: TWO_PI, step: DEG2RAD, subtype: 'angle' } },
      { name: 'scale', label: 'Scale', type: 'vec2' },
    ],
    graphs: ['material']
  }),
};

(['Vector3', 'Vector4', 'Color', 'SamplePoint'] as const).forEach(key => {
  SOURCE_NODE_DEFS[key].graphs = ['material'];
});
